import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { cartService } from '../../services/api'
import { getFinalTotalFromSummary } from './cartSummaryUtils.mjs'

const initialState = {
  items: [],
  loading: false,
  error: null,
  totalItems: 0,
  totalPrice: 0,
  discount: 0,
  appliedCoupon: null,
  shippingCost: 0,
  finalTotal: 0,
  summary: {}
}

export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartService.getCart()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cart')
    }
  }
)

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (data, { rejectWithValue }) => {
    try {
      const response = await cartService.addToCart(data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to cart')
    }
  }
)

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await cartService.removeFromCart(itemId)
      return { itemId, response: response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from cart')
    }
  }
)

export const updateQuantity = createAsyncThunk(
  'cart/updateQuantity',
  async ({ cartItemId, quantity }, { rejectWithValue }) => {
    try {
      const response = await cartService.updateQuantity(cartItemId, quantity)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update quantity')
    }
  }
)

export const updateBdsPrinting = createAsyncThunk(
  'cart/updateBdsPrinting',
  async ({ cartItemId, bdsWillPrint }, { rejectWithValue }) => {
    try {
      const response = await cartService.updateBdsPrinting(cartItemId, bdsWillPrint)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update printing choice')
    }
  }
)

export const applyCoupon = createAsyncThunk(
  'cart/applyCoupon',
  async (code, { rejectWithValue }) => {
    try {
      const response = await cartService.applyCoupon(code)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to apply coupon')
    }
  }
)

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      await cartService.clearCart()
      return true
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear cart')
    }
  }
)

const applyCartSummary = (state, summary, preserveDiscount = false) => {
  state.summary = summary
  state.totalItems = summary.itemCount || 0
  state.totalPrice = summary.subtotal || 0
  state.shippingCost = summary.shippingCost || 0

  if (preserveDiscount) {
    state.finalTotal = getFinalTotalFromSummary(summary, state.discount)
  } else {
    state.discount = 0
    state.appliedCoupon = null
    state.finalTotal = getFinalTotalFromSummary(summary)
  }
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    calculateTotals: (state) => {
      state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0)
      state.totalPrice = state.items.reduce((total, item) => {
        const price = Number(item.unitPrice || item.product?.price || 0)
        return total + (price * item.quantity)
      }, 0)
      state.finalTotal = state.totalPrice - state.discount + state.shippingCost
    },
    setShippingCost: (state, action) => {
      state.shippingCost = action.payload
      state.finalTotal = state.totalPrice - state.discount + state.shippingCost
    },
    removeCoupon: (state) => {
      state.appliedCoupon = null
      state.discount = 0
      state.finalTotal = state.totalPrice - state.discount + state.shippingCost
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false
        // Ajuste para o formato real da API
        state.items = action.payload.obj?.items || []
        applyCartSummary(state, action.payload.obj?.summary || {})
        // Outros campos antigos podem ser mantidos para compatibilidade
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(addToCart.fulfilled, (state, action) => {
        // Usar os dados retornados pela API
        const newCartItem = action.payload.obj?.cartItem
        if (newCartItem) {
          // Verificar se o item já existe no carrinho
          const existingItemIndex = state.items.findIndex(item => item.productId === newCartItem.productId)
          
          if (existingItemIndex !== -1) {
            // Se o item já existe, apenas incrementar a quantidade
            state.items[existingItemIndex].quantity += newCartItem.quantity
          } else {
            // Se o item não existe, adicionar ao carrinho
            state.items.push(newCartItem)
          }
        }
        
        // Sempre recalcular totais após adicionar item
        cartSlice.caseReducers.calculateTotals(state)
      })
      
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload.itemId)
        const summary = action.payload.response?.obj?.summary
        if (summary) {
          applyCartSummary(state, summary)
        } else {
          cartSlice.caseReducers.calculateTotals(state)
        }
      })
      
      .addCase(updateQuantity.fulfilled, (state, action) => {
        // Atualizar o item específico com os dados retornados da API
        const updatedCartItem = action.payload.obj?.cartItem
        if (updatedCartItem) {
          const itemIndex = state.items.findIndex(item => item.id === updatedCartItem.id)
          if (itemIndex !== -1) {
            // Atualizar o item com todos os dados retornados da API
            state.items[itemIndex] = {
              ...state.items[itemIndex],
              ...updatedCartItem,
              // Garantir que os dados do produto sejam mantidos
              product: updatedCartItem.product || state.items[itemIndex].product
            }
          }
        }
        
        // Se a API retornou dados do summary, usar eles para atualizar os totais
        if (action.payload.obj?.summary) {
          applyCartSummary(state, action.payload.obj.summary)
        } else {
          // Recalcular totais baseado nos dados atualizados
          cartSlice.caseReducers.calculateTotals(state)
        }
      })

      .addCase(updateBdsPrinting.fulfilled, (state, action) => {
        const updatedCartItem = action.payload.obj?.cartItem
        if (updatedCartItem) {
          const itemIndex = state.items.findIndex(item => item.id === updatedCartItem.id)
          if (itemIndex !== -1) {
            state.items[itemIndex] = {
              ...state.items[itemIndex],
              ...updatedCartItem,
              product: updatedCartItem.product || state.items[itemIndex].product
            }
          }
        }

        if (action.payload.obj?.summary) {
          applyCartSummary(state, action.payload.obj.summary, true)
        }
      })
      
      .addCase(applyCoupon.fulfilled, (state, action) => {
        state.appliedCoupon = action.payload.coupon
        state.discount = action.payload.discount
        state.finalTotal = state.totalPrice - state.discount + state.shippingCost
      })
      
      .addCase(clearCart.fulfilled, (state) => {
        state.items = []
        state.totalItems = 0
        state.totalPrice = 0
        state.discount = 0
        state.appliedCoupon = null
        state.shippingCost = 0
        state.finalTotal = 0
      })
  }
})

export const { clearError, calculateTotals, setShippingCost, removeCoupon } = cartSlice.actions

// Selectors
export const selectCart = (state) => state.cart
export const selectCartItems = (state) => state.cart.items
export const selectCartLoading = (state) => state.cart.loading
export const selectCartError = (state) => state.cart.error
export const selectCartTotalItems = (state) => state.cart.totalItems
export const selectCartTotalPrice = (state) => state.cart.totalPrice
export const selectCartDiscount = (state) => state.cart.discount
export const selectCartFinalTotal = (state) => state.cart.finalTotal
export const selectAppliedCoupon = (state) => state.cart.appliedCoupon
export const selectCartItemsCount = (state) => state.cart.items.length

export default cartSlice.reducer
